import { Bell, Database, FlaskConical, Home, Mail, Settings, Workflow } from "lucide-react";
import { MouseEvent, ReactNode } from "react";
import { NuqsAdapter } from "nuqs/adapters/react-router/v8";
import { Outlet, useLocation, useNavigate } from "react-router";

import {
  AppLauncher,
  AppShell,
  type AppShellBreadcrumbItem,
  NavRailItem,
  NotificationBell,
  SearchInput,
  VersionStamp,
} from "nazare-ui/components";
import {
  isCurrentAppLink,
  useShellStore,
  useStoredAuth,
} from "nazare-ui/utils";

import { useGlobalSearchInputProps } from "../global-search-context";
import { useProfile } from "../profile-api";
import { runViewTransition } from "../run-view-transition";
import { useSecondaryHeaderContent } from "../secondary-header-context";
import { useAppLauncherManifest } from "../use-app-launcher-manifest";
import dynamoDbLogo from "./data-explorer/assets/amazon-dynamodb.svg";
import s3Logo from "./data-explorer/assets/amazon-s3.svg";
import postgresLogo from "./data-explorer/assets/postgresql.svg";

type NavEntry = {
  to: string;
  label: string;
  /** Longer text shown only when the rail is expanded — see `<NavRailItem>`'s own doc. */
  expandedLabel?: string;
  /** Breadcrumb text for this route, if different from `label` (e.g. the
   * nav rail's short "QA" vs. the breadcrumb's full "QA Automation"). */
  breadcrumbLabel?: string;
  icon: ReactNode;
};

// Primary, scrollable nav list.
const NAV_ENTRIES: NavEntry[] = [
  { to: "/", label: "Home", icon: <Home /> },
  { to: "/workflows", label: "Workflows", icon: <Workflow /> },
  { to: "/inboxes", label: "Inboxes", icon: <Mail /> },
  {
    to: "/qa-automation",
    label: "QA",
    expandedLabel: "QA Automation",
    breadcrumbLabel: "QA Automation",
    icon: <FlaskConical />,
  },
  { to: "/notifications", label: "Notifications", icon: <Bell /> },
  {
    to: "/data-explorer",
    label: "Data",
    expandedLabel: "Data Explorer",
    breadcrumbLabel: "Data Explorer",
    icon: <Database />,
  },
];

// Rendered in the footer below the theme toggle and above identity/version —
// chrome-adjacent destinations (just Settings for now), not primary nav.
const FOOTER_NAV_ENTRIES: NavEntry[] = [
  { to: "/settings", label: "Settings", icon: <Settings /> },
];

// All routes with a nav entry, used to derive the current page's breadcrumb
// trail below — deliberately just this one flat lookup (no route-tree
// walking) since every nav entry here is a single-level child of "/".
const ALL_NAV_ENTRIES = [...NAV_ENTRIES, ...FOOTER_NAV_ENTRIES];

// `/workflows/*` sub-pages (full-page tables, "maximised" from a dashboard
// card) aren't nav entries themselves — they hang off the "Workflows" nav
// entry with a second breadcrumb level instead. Keyed by pathname, same
// flat-lookup approach as `ALL_NAV_ENTRIES`.
const WORKFLOW_SUBPAGE_LABELS: Record<string, string> = {
  "/workflows/runs": "Runs",
  "/workflows/active": "Active runs",
  "/workflows/definitions": "Workflow definitions",
  "/workflows/history": "Workflow status history",
  "/workflows/scheduled": "Scheduled workflows",
  "/workflows/infra": "Infra schedules",
};

// `/qa-automation/*` sub-pages — same "maximised card" convention as
// `WORKFLOW_SUBPAGE_LABELS` above, just hanging off "QA Automation" instead
// of "Workflows".
const QA_SUBPAGE_LABELS: Record<string, string> = {
  "/qa-automation/recent-runs": "Recent runs",
  "/qa-automation/slowest-tests": "Slowest tests",
  "/qa-automation/highest-failure-rate": "Tests with the highest failure rate",
};

/**
 * Identity for the shell's `<IdentityControl>`. Prefers the BFF's live
 * `/user/me` copy (`../profile-api.ts`, the same data `/profile` itself
 * edits) so saving a name/avatar change there shows up in the rail
 * immediately — `useStoredAuth()`'s `user` object is a READ-ONLY
 * localStorage snapshot from nazare-frontend's own login that this app
 * never writes back to, so without this the rail would keep showing
 * whatever you looked like at your last sign-in even after editing your
 * profile here. Falls back to that snapshot while the profile query is
 * still loading (or hasn't got a session to fetch for), so the rail never
 * regresses to a bare "Guest" during that gap. Never throws, even if
 * nobody has ever logged into the main app here.
 */
function useIdentity() {
  const auth = useStoredAuth();
  const user = auth?.user as
    | {
        firstName?: string;
        lastName?: string;
        email?: string;
        avatar?: string | null;
      }
    | null
    | undefined;

  // `enabled: hasStoredSession(auth)` inside useProfile already no-ops this
  // when there's no session at all — safe to call unconditionally here.
  const { data: profileData } = useProfile();
  const profile = profileData?.profile;

  const fullName = profile
    ? [profile.first_name, profile.last_name].filter(Boolean).join(" ")
    : [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const email = profile?.email ?? user?.email;
  const name = fullName || email || "Guest";

  return {
    name,
    subtitle: email && email !== name ? email : undefined,
    avatarUrl: profile ? profile.avatar : (user?.avatar ?? null),
  };
}

/** `/workflows/runs/:runId` — matches the reference app's own truncated
 * "Run wrun_01KWYRK…" breadcrumb style for a run detail page. */
const RUN_DETAIL_PREFIX = "/workflows/runs/";

/** `/qa-automation/run/:runId` — same truncated "Run …" breadcrumb style as
 * the workflows run detail page above, singular "run" to match the URL. */
const QA_RUN_DETAIL_PREFIX = "/qa-automation/run/";

function runDetailCrumbLabel(runId: string): string {
  const truncated = runId.length > 14 ? `${runId.slice(0, 14)}…` : runId;
  return `Run ${truncated}`;
}

/**
 * Current page's breadcrumb trail. Most routes are a single level deep (one
 * crumb, looked up against `ALL_NAV_ENTRIES` by exact pathname match).
 * `/workflows/*` "maximised" sub-pages get a second crumb back to
 * "Workflows"; `/workflows/runs/:runId` goes one level deeper still, back
 * through "Runs". Falls back to a generic single crumb for anything that
 * matched none of these (e.g. the 404 catch-all).
 */
function useBreadcrumbItems(): AppShellBreadcrumbItem[] {
  const location = useLocation();
  const navigate = useNavigate();

  const goTo = (to: string) => (event: MouseEvent) => {
    event.preventDefault();
    runViewTransition(() => navigate(to));
  };

  if (location.pathname === "/data-explorer") {
    return [{ title: "Data Explorer" }];
  }

  if (location.pathname.startsWith("/data-explorer/")) {
    const current = new URLSearchParams(location.search);
    const activeSource = location.pathname.split("/")[2] ?? "postgres";
    const sourceLabels: Record<string, string> = {
      postgres: "PostgreSQL",
      dynamodb: "DynamoDB",
      s3: "Amazon S3",
    };
    const sourceLogos: Record<string, string> = {
      postgres: postgresLogo,
      dynamodb: dynamoDbLogo,
      s3: s3Logo,
    };
    const incompatibleParams = [
      "resource",
      "schema",
      "field",
      "op",
      "value",
      "prefix",
      "cursor",
      "object",
      "preview",
      "sort",
      "direction",
    ];
    const options = [
      { value: "postgres", title: "PostgreSQL" },
      { value: "dynamodb", title: "DynamoDB" },
      { value: "s3", title: "Amazon S3" },
    ].map((source) => {
      const next = new URLSearchParams(current);
      next.delete("source");
      incompatibleParams.forEach((param) => next.delete(param));
      const query = next.toString();
      const uri = `/data-explorer/${source.value}${query ? `?${query}` : ""}`;
      return {
        title: source.title,
        uri,
        selected: activeSource === source.value,
        icon: <img src={sourceLogos[source.value]} alt="" />,
        onClick: goTo(uri),
      };
    });
    return [
      {
        title: "Data Explorer",
        uri: "/data-explorer",
        onClick: goTo("/data-explorer"),
      },
      { title: sourceLabels[activeSource] ?? "PostgreSQL", options },
    ];
  }

  if (location.pathname.startsWith(RUN_DETAIL_PREFIX)) {
    const runId = location.pathname
      .slice(RUN_DETAIL_PREFIX.length)
      .split("/")[0];
    return [
      { title: "Workflows", uri: "/workflows", onClick: goTo("/workflows") },
      { title: "Runs", uri: "/workflows/runs", onClick: goTo("/workflows/runs") },
      { title: runDetailCrumbLabel(runId) },
    ];
  }

  if (location.pathname.startsWith(QA_RUN_DETAIL_PREFIX)) {
    // Unlike the workflows run-detail route above, this one has further
    // path segments after the run id itself (`/results/:testId`, `/logs`,
    // `/artefacts` — see router.tsx) — only take the first segment, not
    // everything after the prefix.
    const runId = location.pathname.slice(QA_RUN_DETAIL_PREFIX.length).split("/")[0];
    return [
      { title: "QA Automation", uri: "/qa-automation", onClick: goTo("/qa-automation") },
      {
        title: "Recent runs",
        uri: "/qa-automation/recent-runs",
        onClick: goTo("/qa-automation/recent-runs"),
      },
      { title: runDetailCrumbLabel(runId) },
    ];
  }

  // The bell dropdown's personal inbox — same "Notifications" parent crumb
  // as the admin user-lookup tool below (they share the nav rail's
  // "Notifications" entry/highlight), one level deeper as "Mine".
  if (location.pathname === "/notifications/all") {
    return [
      { title: "Notifications", uri: "/notifications", onClick: goTo("/notifications") },
      { title: "Mine" },
    ];
  }

  if (
    location.pathname === "/settings/me" ||
    location.pathname === "/settings/system"
  ) {
    return [
      {
        title: "Settings",
        uri: "/settings/me",
        onClick: goTo("/settings/me"),
      },
      { title: location.pathname.endsWith("/system") ? "System" : "Me" },
    ];
  }

  // The admin users×types matrix — one level under the "Notifications"
  // landing page (which now lives at the bare `/notifications`).
  if (location.pathname === "/notifications/users") {
    return [
      { title: "Notifications", uri: "/notifications", onClick: goTo("/notifications") },
      { title: "Users" },
    ];
  }

  const subpageLabel = WORKFLOW_SUBPAGE_LABELS[location.pathname];
  if (subpageLabel) {
    return [
      { title: "Workflows", uri: "/workflows", onClick: goTo("/workflows") },
      { title: subpageLabel },
    ];
  }

  const qaSubpageLabel = QA_SUBPAGE_LABELS[location.pathname];
  if (qaSubpageLabel) {
    return [
      { title: "QA Automation", uri: "/qa-automation", onClick: goTo("/qa-automation") },
      { title: qaSubpageLabel },
    ];
  }

  // `/profile` isn't a nav rail entry either (it hangs off the identity
  // control at the rail's bottom, not an icon in `NAV_ENTRIES`/
  // `FOOTER_NAV_ENTRIES`), so it's not in `ALL_NAV_ENTRIES` — same
  // standalone-crumb treatment as `/changelog` below.
  if (location.pathname === "/profile") {
    return [{ title: "Profile" }];
  }

  // `/changelog` isn't a nav rail entry (it hangs off the rail's version
  // stamp, not an icon), so it's not in `ALL_NAV_ENTRIES` — a standalone
  // single crumb, no parent, same as this being a top-level destination.
  if (location.pathname === "/changelog") {
    return [{ title: "Changelog" }];
  }

  const entry = ALL_NAV_ENTRIES.find((item) => item.to === location.pathname);
  return [{ title: entry?.breadcrumbLabel ?? entry?.label ?? "Not found" }];
}

/**
 * Root layout route: the shared `<AppShell>` (nav rail + top header) around
 * an `<Outlet>` for whichever page route matched. The first real consumer
 * of `nazare-ui`'s app-shell components.
 */
export function RootLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const identity = useIdentity();
  const breadcrumbItems = useBreadcrumbItems();
  const secondaryHeaderContent = useSecondaryHeaderContent();
  const searchInputProps = useGlobalSearchInputProps();
  const appLauncherManifest = useAppLauncherManifest();
  const currentUrl = window.location.href;
  const currentApp = appLauncherManifest.find((app) =>
    isCurrentAppLink(app.link, currentUrl),
  );
  const appLauncherItems = appLauncherManifest.map((app) => ({
    label: app.name,
    href: app.link,
    icon: <img src={app.icon} alt="" />,
    active: isCurrentAppLink(app.link, currentUrl),
  }));

  // Nav rail expanded/collapsed is app-wide chrome state, so it's backed by
  // `nazare-ui`'s shared shell-state store (not a local/uncontrolled
  // `<AppShell>` default) — this is what lets the rail's collapsed state
  // stay consistent across the app (e.g. multiple mounts, or future
  // consumers of the same store) instead of being reset per-mount.
  const navRailCollapsed = useShellStore((state) => state.navRailCollapsed);
  const setNavRailCollapsed = useShellStore(
    (state) => state.setNavRailCollapsed,
  );

  const renderNavEntry = (entry: NavEntry) => {
    const active =
      entry.to === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(entry.to);

    return (
      <NavRailItem
        key={entry.to}
        icon={entry.icon}
        label={entry.label}
        expandedLabel={entry.expandedLabel}
        active={active}
        href={entry.to}
        onClick={(event) => {
          // Only intercept a plain left-click for client-side navigation —
          // preventDefault() was unconditionally blocking the browser's
          // native cmd/ctrl+click ("open in new tab") and middle-click
          // ("open in background tab") behavior on what's a real <a href>.
          // Let those fall through to the browser untouched.
          const isModifiedClick =
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            event.button !== 0;
          if (isModifiedClick) return;
          event.preventDefault();
          runViewTransition(() => navigate(entry.to));
        }}
      />
    );
  };

  const expanded = !navRailCollapsed;

  return (
    <AppShell
      logo={
        <img
          src={currentApp?.icon ?? "/favicon.svg"}
          alt=""
          width="16"
          height="19"
        />
      }
      logoLabel={`PCG ${currentApp?.name ?? "Platform"}`}
      navExpanded={expanded}
      onNavExpandedChange={(next) => setNavRailCollapsed(!next)}
      identityName={identity.name}
      identitySubtitle={identity.subtitle}
      identityAvatarUrl={identity.avatarUrl}
      identityActive={location.pathname === "/profile"}
      onIdentityClick={() => navigate("/profile")}
      navItems={NAV_ENTRIES.map(renderNavEntry)}
      footerNavItems={FOOTER_NAV_ENTRIES.map(renderNavEntry)}
      appLauncher={
        appLauncherItems.length > 0 ? (
          <AppLauncher expanded={expanded} items={appLauncherItems} />
        ) : undefined
      }
      breadcrumbItems={breadcrumbItems}
      search={<SearchInput {...searchInputProps} />}
      notifications={<NotificationBell unreadCount={0} />}
      secondaryHeader={secondaryHeaderContent}
      versionStamp={
        <VersionStamp
          version={__APP_VERSION__}
          expanded={expanded}
          active={location.pathname === "/changelog"}
          onClick={(event) => {
            const isModifiedClick =
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey ||
              event.button !== 0;
            if (isModifiedClick) return;
            event.preventDefault();
            runViewTransition(() => navigate("/changelog"));
          }}
        />
      }
    >
      <NuqsAdapter>
        <Outlet />
      </NuqsAdapter>
    </AppShell>
  );
}
